import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Row, Segmented, Spin } from "antd";

import { controller } from "../../Login/controller";
import { money } from "./money";
import { offerEndsText, offerFor, offerSavingText } from "./offerDisplay";
import { cardTermsLine } from "./planTerms";
import "./checkout.css";

/**
 * The subscription plan grid — the ONE implementation.
 *
 * This used to live inline in Login/component/Purchase.js, which made it
 * reachable only from the onboarding wizard: there was no way to buy a plan
 * afterwards at all (Membership/Subscriptions.js, which the usage-overage modal
 * links to, is a legacy invoice table). Extracted here so onboarding, Billing and
 * the Choose-a-plan modal show the same plans, priced the same way, forever.
 *
 * Selection only — the caller decides what "subscribe" means; the card step
 * lives in MultiOfficeCheckout (see useHelcimReturn for the round-trip note).
 */

export const PURPLE = "#6b43B5";
const POPULAR_FAMILY = "Pro";

const fmtLimit = (v) => (v === null || v === undefined ? "Unlimited" : v);

const Feature = ({ text }) => (
  <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 6, fontSize: 13 }}>
    <span style={{ color: PURPLE, fontWeight: 700 }}>✓</span>
    <span style={{ color: "#555" }}>{text}</span>
  </div>
);

function PlanCard({ fam, variant: v, selected, onPick, compact, readOnly }) {
  const offer = offerFor(v);
  return (
    <Card
      hoverable={!readOnly}
      onClick={readOnly ? undefined : () => onPick(v)}
      style={{
        borderColor: selected ? PURPLE : "#eee",
        borderWidth: selected ? 2 : 1,
        borderRadius: 12,
        height: "100%",
        cursor: readOnly ? "default" : "pointer",
      }}
      bodyStyle={{ padding: compact ? 14 : 20 }}
    >
      <div style={{ fontSize: compact ? 17 : 20, fontWeight: 700, color: PURPLE }}>{fam.family}</div>
      <div style={{ marginTop: 8 }}>
        {offer && offer.listPrice ? (
          <span className="pp-list-price">${money(offer.listPrice)}</span>
        ) : null}
        <span style={{ fontSize: compact ? 22 : 28, fontWeight: 700 }}>${money(v.price)}</span>
        <span style={{ color: "#888" }}>/mo</span>
      </div>
      {/* An annual plan buys the YEAR: the whole term is charged at the start
          of it. The headline stays the per-month rate, because that is how
          plans are compared, but the line under it names the money that
          actually leaves the account today. */}
      <div style={{ fontSize: 12, color: "#888", minHeight: 18 }}>{cardTermsLine(v)}</div>
      {offer ? (
        // Under the price, not beside the name: the Most Popular ribbon sits there.
        <div className="pp-offer-note">
          <span className="pp-offer-tag">{offer.label}</span>{" "}
          <span style={{ whiteSpace: "nowrap" }}>{offerSavingText(offer)}</span>
          {offer.endsOn ? <div className="pp-offer-ends">{offerEndsText(offer.endsOn)}</div> : null}
        </div>
      ) : null}
      <div style={{ margin: "14px 0", borderTop: "1px solid #f0f0f0" }} />
      <Feature text={`Up to ${fmtLimit(v.features.limit_ftc_daily)} verifications / day`} />
      <Feature text={`Up to ${fmtLimit(v.features.limit_ftc_monthly)} verifications / month`} />
      {!compact && (fam.descriptions || []).filter(Boolean).map((d, i) => <Feature key={i} text={d} />)}
      {readOnly ? null : (
        <Button
          block
          type={selected ? "primary" : "default"}
          style={selected ? { background: PURPLE, borderColor: PURPLE, marginTop: 14 } : { marginTop: 14 }}
          onClick={(e) => {
            e.stopPropagation();
            onPick(v);
          }}
        >
          {selected ? "Selected" : "Select"}
        </Button>
      )}
    </Card>
  );
}

/**
 * Props:
 *   officeId   price the plans for this office (its offers, its special plans)
 *   value / onChange(tierId, variant)
 *              controlled selection — used by the multi-office checkout, where
 *              each office row keeps its own choice. Without onChange the picker
 *              keeps its own state and shows its submit button (onSubscribe).
 *   readOnly   plans shown, nothing selectable (staff who cannot buy)
 */
export default function PlanPicker({
  onSubscribe,
  submitting = false,
  submitLabel = "Subscribe & Pay",
  compact = false,
  officeId = null,
  value,
  onChange,
  readOnly = false,
}) {
  const controlled = typeof onChange === "function";
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(false);
  const [billingCycle, setBillingCycle] = useState("annual"); // annual default — it is the better deal
  const [ownTierId, setOwnTierId] = useState(null);
  const chosenTierId = controlled ? value || null : ownTierId;

  const families = useMemo(() => (catalog && catalog.families) || [], [catalog]);
  const specialFamilies = useMemo(() => (catalog && catalog.special_families) || [], [catalog]);
  const allFamilies = useMemo(() => families.concat(specialFamilies), [families, specialFamilies]);

  const variantFor = useCallback(
    (fam) => (billingCycle === "annual" ? fam.annual : fam.monthly) || fam.monthly || fam.annual,
    [billingCycle],
  );

  const loadCatalog = useCallback(async () => {
    setCatalogError(false);
    try {
      // These controllers merge `status` onto the parsed json and return that —
      // there is no {json} wrapper.
      const res = await controller.getSubscriptionCatalog(officeId);
      if (res && res.status < 250) setCatalog(res);
      else setCatalogError(true);
    } catch (e) {
      setCatalogError(true);
    }
  }, [officeId]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const pick = (variant) => {
    if (readOnly || !variant) return;
    if (controlled) onChange(variant.id, variant);
    else setOwnTierId(variant.id);
  };

  // Keep the selection on the same family when the cycle flips, so toggling
  // Monthly/Annual doesn't silently clear what they picked.
  const onToggleCycle = (cycle) => {
    const current = allFamilies.find(
      (f) => (f.monthly && f.monthly.id === chosenTierId) || (f.annual && f.annual.id === chosenTierId),
    );
    setBillingCycle(cycle);
    if (current) {
      const v = cycle === "annual" ? current.annual : current.monthly;
      if (v) pick(v);
    }
  };

  if (catalogError) {
    return (
      <div style={{ padding: 20 }}>
        <p>Couldn't load the plans.</p>
        <Button onClick={loadCatalog}>Retry</Button>
      </div>
    );
  }
  if (!catalog) return <Spin />;

  const grid = (list) => (
    <Row gutter={[compact ? 12 : 24, compact ? 12 : 24]} justify="center">
      {list.map((fam) => {
        const v = variantFor(fam);
        if (!v) return null;
        const card = (
          <PlanCard fam={fam} variant={v} selected={chosenTierId === v.id} onPick={pick}
                    compact={compact} readOnly={readOnly} />
        );
        return (
          <Col xs={24} sm={8} key={fam.family} style={{ marginBottom: compact ? 8 : 16 }}>
            {fam.family === POPULAR_FAMILY ? (
              <Badge.Ribbon text="Most Popular" color={PURPLE}>{card}</Badge.Ribbon>
            ) : (
              card
            )}
          </Col>
        );
      })}
    </Row>
  );

  return (
    <>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: compact ? 16 : 24 }}>
        <Segmented
          value={billingCycle}
          onChange={onToggleCycle}
          options={[
            { label: "Monthly", value: "monthly" },
            { label: "Annual — save more", value: "annual" },
          ]}
        />
      </div>

      {grid(families)}

      {specialFamilies.length ? (
        <div className="pp-special">
          <div className="pp-special-title">Special plans for this office</div>
          {grid(specialFamilies)}
        </div>
      ) : null}

      {controlled || readOnly ? null : (
        <div style={{ marginTop: compact ? 18 : 28, maxWidth: 440, marginLeft: "auto", marginRight: "auto" }}>
          <Button
            block
            type="primary"
            disabled={!chosenTierId}
            loading={submitting}
            onClick={() => {
              if (!onSubscribe) return;
              // Hand the variant up too: the checkout keeps the plan and its price
              // on screen while the customer fills in billing details.
              const fam = allFamilies.find(
                (f) => (f.monthly && f.monthly.id === chosenTierId) || (f.annual && f.annual.id === chosenTierId),
              );
              const variant = fam ? [fam.monthly, fam.annual].find((x) => x && x.id === chosenTierId) : null;
              onSubscribe(chosenTierId, variant);
            }}
            style={{ background: PURPLE, borderColor: PURPLE }}
          >
            {submitLabel}
          </Button>
        </div>
      )}
    </>
  );
}
